/**
 * Operational honesty on AI.
 *
 * Every AI invocation in this app is *advisory*: it never overrides a human
 * decision, never auto-charges, never auto-rejects a task, never auto-closes
 * a maintenance issue. This module centralizes:
 *
 *  - The label / wording shown to humans next to AI output (so we never ship
 *    AI conclusions without the "Advisory" framing).
 *  - A structured client-side log of every AI call (kind, confidence, model,
 *    propertyId, timing) so we can audit reliability without committing to a
 *    new DB table yet.
 *
 * When we're ready to persist these events to Supabase, swap
 * `defaultEmitAiEvent` for an `ai_events` insert; call sites do not need to
 * change.
 */

export type AiAdvisoryKind =
  | "inventory_photo_scan"
  | "inventory_walkthrough"
  | "compare_cleaning_photos"
  | "analyze_receipt_total"
  | "vendor_work_order_ai"
  | "translate_text"
  | "checklist_media_recommendations";

export interface AiAdvisoryEvent {
  kind: AiAdvisoryKind;
  /** Optional property scope so we can group by property in audits. */
  propertyId?: string | null;
  /** Optional task scope. */
  cleaningTaskId?: string | null;
  /** Model name string when known (e.g. "gpt-4o-mini"). */
  model?: string | null;
  /** Best-effort confidence in [0, 1]. Pass null when the model didn't return one. */
  confidence?: number | null;
  /** ms elapsed (start to end of invoke). */
  durationMs?: number | null;
  /** Optional outcome label for triage ("ok", "low_confidence", "error", etc.). */
  outcome?: string | null;
  /** Free-form extra context. Keep small — this is logged synchronously. */
  extra?: Record<string, unknown>;
}

export const AI_DISCLAIMER_TEXT =
  "Automated AI suggestion. Confidence varies; verify before acting. Not legal proof.";

/** Display label for the kind, used inside badges/headers. */
export const AI_KIND_LABEL: Record<AiAdvisoryKind, string> = {
  inventory_photo_scan: "AI inventory scan",
  inventory_walkthrough: "AI walkthrough",
  compare_cleaning_photos: "AI photo check",
  analyze_receipt_total: "AI receipt total",
  vendor_work_order_ai: "AI vendor assist",
  translate_text: "AI translation",
  checklist_media_recommendations: "AI proof guide",
};

type EmitFn = (event: AiAdvisoryEvent & { ts: string }) => void;

const defaultEmitAiEvent: EmitFn = (event) => {
  // Structured log so we (or a downstream tool) can grep / pipe these into an
  // analytics sink. Kept at `info` so it's filterable without being noisy.
  // Do NOT include user PII (emails, names) in `extra`.
  // eslint-disable-next-line no-console
  console.info("[ai-advisory]", JSON.stringify(event));
};

let activeEmit: EmitFn = defaultEmitAiEvent;

/** Replace the emitter (e.g. wire to Supabase insert) without touching call sites. */
export function setAiAdvisoryEmitter(fn: EmitFn | null) {
  activeEmit = fn ?? defaultEmitAiEvent;
}

/**
 * Record an AI advisory event. Returns the event for convenience.
 *
 * Usage:
 *   const t0 = performance.now();
 *   const result = await runAiCall();
 *   logAiAdvisoryEvent({
 *     kind: "compare_cleaning_photos",
 *     propertyId,
 *     confidence: result.summary?.avg_confidence,
 *     model: "gpt-4o-mini",
 *     durationMs: performance.now() - t0,
 *     outcome: "ok",
 *   });
 */
export function logAiAdvisoryEvent(event: AiAdvisoryEvent): AiAdvisoryEvent {
  try {
    activeEmit({ ...event, ts: new Date().toISOString() });
  } catch (err) {
    // Never let logging break an AI flow.
    // eslint-disable-next-line no-console
    console.warn("[ai-advisory] emitter failed:", err);
  }
  return event;
}

/** Format a numeric confidence in [0, 1] as a percent badge, or "—" when unknown. */
export function formatAiConfidence(confidence: number | null | undefined): string {
  if (confidence == null || !Number.isFinite(confidence)) return "—";
  const clamped = Math.max(0, Math.min(1, confidence));
  return `${Math.round(clamped * 100)}%`;
}
